if (!customElements.get("recipient-form")) {
  customElements.define(
    "recipient-form",
    class RecipientForm extends HTMLElement {
      constructor() {
        super();

        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-Fields-Live-Region-${this.dataset.section}`);
        // Turn on the form switch
        this.checkboxInput = this.querySelector(`#Recipient-Checkbox-${this.dataset.section}`);

        if (this.checkboxInput) {
          this.checkboxInput.disabled = false;
          this.checkboxInput.addEventListener(
            "change",
            this.onChange.bind(this),
          );
        }

        // Form elements
        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.section}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.section}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.section}`);
        this.send_onInput = this.querySelector(`#Recipient-send_on-${this.dataset.section}`);

        // Inline error spans for name and email
        this.nameInlineError = this.querySelector(`#Recipient-name-inline-error-${this.dataset.section}`);
        this.emailInlineError = this.querySelector(`#Recipient-email-inline-error-${this.dataset.section}`);

        // Character counter 
        this.messageCounter = this.querySelector(`#Recipient-message-counter-${this.dataset.section}`);
        this._maxCharsMessage = parseInt(
          this.messageInput?.getAttribute('maxlength') || '200',
          10
        );
        this._onMessageInput = this._updateCharCounter.bind(this);
        if (this.messageInput && this.messageCounter) {
          this.messageInput.addEventListener('input', this._onMessageInput);
        }

        // Set time zone
        this.offsetProperty = document.getElementById(`Recipient-Timezone-Offset-${this.dataset.section}`);
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.hiddenControlField = document.getElementById(`Recipient-Control-${this.dataset.section}`);
        this.hiddenControlField.disabled = true;

        // Display error
        this.errorMessageWrapper = this.querySelector(".error-message-wrapper");
        this.errorMessageList = this.errorMessageWrapper?.querySelector(".message-list");

        // Product variant ID
        this.currentProductVariantId = this.dataset.productVariantId;

        // Attach blur-time inline validation for name and email
        this.nameInput?.addEventListener("blur", () => this.validateName());
        this.emailInput?.addEventListener("blur", () => this.validateEmail());

        // Date input setup 
        this.dateInput = this.querySelector(`#Recipient-send_on-${this.dataset.section}`);
        if (this.dateInput) {
          // Block past dates by setting today as the minimum selectable date
          const dateToday = new Date().toISOString().split("T")[0];
          this.dateInput.setAttribute("min", dateToday);
        }

        // Bound handler references kept so they can be removed later
        this._onDateClick = this._handleDateClick.bind(this);
        this._onDateKeydown = this._handleDateKeydown.bind(this);
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        // Submit shopping cart updates
        this.cartUpdateUnsubscriber = webvista.subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          (event) => {
            if (
              event.source === "product-form" &&
              event.productVariantId.toString() === this.currentProductVariantId
            ) {
              this.resetRecipientForm();
            }
          },
        );

        // Failed to submit shopping cart.
        this.cartErrorUnsubscriber = webvista.subscribe(
          PUB_SUB_EVENTS.cartError,
          (event) => {
            if (
              event.source === "product-form" &&
              event.productVariantId.toString() === this.currentProductVariantId
            ) {
              this.displayErrorMessage(event.errors);
            }
          },
        );

        // Modify variant ID
        this.variantChangeUnsubscriber = webvista.subscribe(
          PUB_SUB_EVENTS.variantChange,
          (event) => {
            if (event.data.section === this.dataset.section) {
              this.currentProductVariantId = event.data.variant.id.toString();
            }
          },
        );

        // Date input events
        if (this.dateInput) {
          this.dateInput.addEventListener("click", this._onDateClick);
          this.dateInput.addEventListener("keydown", this._onDateKeydown);
        }
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }

        // Date input cleanup
        if (this.dateInput) {
          this.dateInput.removeEventListener("click", this._onDateClick);
          this.dateInput.removeEventListener("keydown", this._onDateKeydown);
        }

        if (this.messageInput && this._onMessageInput) {
          this.messageInput.removeEventListener('input', this._onMessageInput);
        }
      }

      /* ====== Open the native date picker on click (required in some browsers where clicking the text area of the input won't open the picker). ====== */
      _handleDateClick() {
        this.dateInput.showPicker?.();
      }

      /* ====== Prevent manual keyboard entry inside the date field so users can only select a date via the picker, avoiding invalid input. ====== */
      _handleDateKeydown(e) {
        e.preventDefault();
      }

      onChange() {
        if (this.checkboxInput.checked) {
          this.enableInputFields();
          this.recipientFieldsLiveRegion.innerText = window["accessibilityStrings"]["recipientFormExpanded"];
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          this.recipientFieldsLiveRegion.innerText = window["accessibilityStrings"]["recipientFormCollapsed"];
        }
      }

      inputFields() {
        return [
          this.emailInput,
          this.nameInput,
          this.messageInput,
          this.send_onInput,
        ].filter(Boolean);
      }

      disableFields() {
        return [...this.inputFields(), this.offsetProperty].filter(Boolean);
      }

      // Clear form inputs
      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ""));
        // Also clear inline errors when the form is reset
        this.clearInlineError(this.nameInput, this.nameInlineError);
        this.clearInlineError(this.emailInput, this.emailInlineError);
        // Reset character counter
        if (this.messageCounter) {
          this.messageCounter.textContent = `${this._maxCharsMessage} characters remaining`;
        }
      }

      // Activate the input form
      enableInputFields() {
        this.disableFields().forEach((field) => (field.disabled = false));
      }

      // Disable input form
      disableInputFields() {
        this.disableFields().forEach((field) => (field.disabled = true));
      }

      /* ====== Returns true if the recipient checkbox is checked (form is active).
       * Used to guard validation so disabled/hidden fields are never validated. ====== */
      isRecipientFormActive() {
        return !!(this.checkboxInput && this.checkboxInput.checked);
      }

      /* ====== Public method called by product-form.js before cart submission.
       * Validates all active recipient fields and returns false if any fail.
       * Returns true immediately when the recipient form is not active. ====== */
      validateRecipientForm() {
        if (!this.isRecipientFormActive()) return true;
        const nameValid  = this.validateName();
        const emailValid = this.validateEmail();
        return nameValid && emailValid;
      }

      /* ====== Validate name field — required, minlength 2.
       * Skips validation silently if the recipient form is not active. ====== */
      validateName() {
        if (!this.nameInput) return true;
        // Do not validate when the form is collapsed / fields are disabled
        if (!this.isRecipientFormActive()) return true;

        const val = this.nameInput.value.trim();

        if (val === "") {
          this.showInlineError(
            this.nameInput,
            this.nameInlineError,
            "Name is required.",
          );
          return false;
        }

        if (val.length < 2) {
          this.showInlineError(
            this.nameInput,
            this.nameInlineError,
            "Name must be at least 2 characters.",
          );
          return false;
        }

        this.clearInlineError(this.nameInput, this.nameInlineError);
        return true;
      }

      /* ====== Validate email field — required, valid format.
       * Skips validation silently if the recipient form is not active. ====== */
      validateEmail() {
        if (!this.emailInput) return true;
        // Do not validate when the form is collapsed / fields are disabled
        if (!this.isRecipientFormActive()) return true;

        const val = this.emailInput.value.trim();
        const pattern = /^.+@.+\.[a-zA-Z]{2,}$/;

        if (val === "") {
          this.showInlineError(
            this.emailInput,
            this.emailInlineError,
            "Enter a valid email address.",
          );
          return false;
        }

        if (!pattern.test(val)) {
          this.showInlineError(
            this.emailInput,
            this.emailInlineError,
            "Please enter a valid email address.",
          );
          return false;
        }

        this.clearInlineError(this.emailInput, this.emailInlineError);
        return true;
      }

      /* ====== Updates the character-remaining counter as the user types. ====== */
      _updateCharCounter() {
        if (!this.messageCounter || !this.messageInput) return;
        const remaining = this._maxCharsMessage - this.messageInput.value.length;
        this.messageCounter.textContent = `${remaining} characters remaining`;
      }

      /* ====== Show an inline error message beneath a field ====== */
      showInlineError(inputEl, errorEl, message) {
        if (!errorEl || !inputEl) return;
        errorEl.textContent = message;
        errorEl.hidden = false;
        inputEl.setAttribute("aria-invalid", "true");
        inputEl.setAttribute("aria-describedby", errorEl.id);
        // inputEl.closest(".field")?.classList.add("field-error");
      }

      /* ====== Clear an inline error message beneath a field ====== */
      clearInlineError(inputEl, errorEl) {
        if (!errorEl || !inputEl) return;
        errorEl.textContent = "";
        errorEl.hidden = true;
        inputEl.removeAttribute("aria-invalid");
        inputEl.removeAttribute("aria-describedby");
        // inputEl.closest(".field")?.classList.remove("field-error");
      }

      /* ====== Display error message ====== */
      displayErrorMessage(errors) {
        this.clearErrorMessage();

        this.errorMessageWrapper.hidden = false;
        this.errorMessageWrapper.focus();

        if (typeof errors === "object") {
          return Object.entries(errors).forEach(([key, value]) => {
            const errorMessageId = `Recipient-Form-${key}-${this.dataset.section}-Error`;
            const fieldSelector = `#Recipient-${key}-${this.dataset.section}`;
            const message = `${value.join(", ")}`;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(
                this.createErrorListItem(
                  fieldSelector,
                  errorMessageId,
                  message,
                ),
              );
            }

            const inputElement = this[`${key}Input`];
            if (!inputElement) return;

            inputElement.setAttribute("aria-invalid", true);
            inputElement.setAttribute("aria-describedby", errorMessageId);
            // inputElement.closest(".field")?.classList.add("field-error");
          });
        }
      }

      /* ====== Create error record ====== */
      createErrorListItem(target, id, message) {
        const li = document.createElement("li");
        li.id = id;
        li.textContent = message;
        return li;
      }

      /* ====== Clear error messages ====== */
      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true;
        if (this.errorMessageList) this.errorMessageList.innerHTML = "";

        this.inputFields().forEach((inputElement) => {
          inputElement.removeAttribute("aria-invalid");
          inputElement.removeAttribute("aria-describedby");
          // inputElement.closest(".field")?.classList.remove("field-error");
        });
      }

      /* ====== Reset form ====== */
      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          this.recipientFieldsLiveRegion.innerText = window["accessibilityStrings"]["recipientFormCollapsed"];
        }
      }
    },
  );
}